import { ChangeDetectorRef, Component, OnInit, ViewChild, ViewEncapsulation, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25TableComponent } from "../../s25-table/s25.table.component";
import { S25TableModule } from "../../s25-table/s25.table.module";
import { S25DatepickerModule } from "../../s25-datepicker/s25.datepicker.module";
import { S25LoadingInlineModule } from "../../s25-loading/s25.loading.inline.module";
import { Table } from "../../s25-table/Table";
import { UserprefService } from "../../../services/userpref.service";
import { S25Datefilter } from "../../s25-dateformat/s25.datefilter.service";
import { GenericTableFadePreviewComponent } from "../../s25-table/generics/generic.table.fade.preview.component";
import { Workflow } from "../service/Workflow";
import { WorkflowService } from "../service/workflow.service";

@TypeManagerDecorator("workflow-action-history-page")
@Component({
    selector: "workflow-action-history-page",
    template: `
        @if (init()) {
            <div class="workflow-action-history-page">
                <div class="page-header">
                    <h1>Workflow Action History</h1>
                    <div class="page-controls">
                        <label class="filter-control">
                            <input type="checkbox" [(ngModel)]="showErrorsOnly" />
                            Show errors only
                        </label>
                        <label class="filter-control">
                            <span>From:</span>
                            <s25-datepicker-inline
                                [date]="fromDate()"
                                (dateChange)="fromDate.set($event)"
                            ></s25-datepicker-inline>
                        </label>
                        <label class="filter-control">
                            <span>To:</span>
                            <s25-datepicker-inline
                                [date]="toDate()"
                                (dateChange)="toDate.set($event)"
                            ></s25-datepicker-inline>
                        </label>
                        <button class="aw-button aw-button--primary" (click)="refreshTable()">Search</button>
                    </div>
                </div>

                @if (errorMessage()) {
                    <div class="error-alert">
                        <div class="error-icon">⚠️</div>
                        <div class="error-text">{{ errorMessage() }}</div>
                    </div>
                }

                <s25-ng-table
                    #historyTable
                    [caption]="'Workflow Action History'"
                    [unlimitedWidth]="true"
                    [columnSortable]="true"
                    [dataSource]="tableConfig"
                    [hasTotalRowCount]="true"
                    [hasRefresh]="true"
                    [showHeaderWhenNoData]="true"
                ></s25-ng-table>
            </div>
        } @else {
            <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
        }
    `,
    styles: `
        .workflow-action-history-page {
            padding: 20px;
        }

        .page-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
        }

        .page-controls {
            display: flex;
            gap: 20px;
            align-items: center;
        }

        .filter-control {
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .error-alert {
            display: flex;
            align-items: center;
            background-color: #fee;
            border: 1px solid #fcc;
            border-radius: 4px;
            padding: 12px;
            margin-bottom: 20px;
        }

        .error-icon {
            margin-right: 8px;
        }

        .error-text {
            flex: 1;
        }
    `,
    standalone: true,
    imports: [CommonModule, FormsModule, S25TableModule, S25DatepickerModule, S25LoadingInlineModule],
    encapsulation: ViewEncapsulation.Emulated,
})
export class WorkflowActionHistoryPageComponent implements OnInit {
    @ViewChild("historyTable") tableComponent!: S25TableComponent;

    init = signal(false);
    history = signal<Workflow.WorkflowActionHistory[]>([]);
    showErrorsOnly = signal(false);
    dateFormat: string = "";
    errorMessage = signal<string>("");

    // Date models for date pickers
    fromDate = signal<Date | null>(null);
    toDate = signal<Date | null>(null);

    tableConfig: Table.DataSource;

    constructor(private cd: ChangeDetectorRef) {}

    async ngOnInit() {
        this.dateFormat = await UserprefService.getS25DateTimeformat();

        // Set default date range to last 30 days
        const today = new Date();
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);

        this.fromDate.set(thirtyDaysAgo);
        this.toDate.set(today);

        this.initializeTableConfig();

        this.init.set(true);
    }

    private initializeTableConfig() {
        const columns: Table.Column[] = [
            { id: "workflowItem", header: "Workflow Item" },
            { id: "workflowItemName", header: "Item Name" },
            { id: "actionType", header: "Action Type" },
            { id: "actionCode", header: "Action Code" },
            { id: "status", header: "Status" },
            { id: "executionDate", header: "Execution Date" },
            { id: "errorMessage", header: "Error Message" },
            { id: "httpResponseCode", header: "Response Code" },
        ];

        this.tableConfig = {
            type: "unpaginated",
            columns: columns,
            dataSource: this.getHistoryData,
        };
    }

    getHistoryData = async (): Promise<Table.DataSourceResponse> => {
        await this.loadHistory();

        // Filter by errors only if checkbox is checked
        let filteredHistory = this.history();
        if (this.showErrorsOnly()) {
            filteredHistory = filteredHistory.filter((item) => item.status === "ERROR");
        }

        return {
            rows: filteredHistory.map((item) => ({
                id: item.workflowActionHistoryId?.toString() || "",
                name: `${item.workflowItemName} - ${item.actionCode} (${item.actionType})`,
                cells: {
                    executionDate: { text: this.formatDate(item.executionDate) },
                    status: { text: item.status || "N/A" },
                    actionType: { text: item.actionType },
                    actionCode: { text: item.actionCode || "N/A" },
                    workflowItem: { text: `${item.workflowItemTypeId}:${item.workflowItemId}` },
                    workflowItemName: { text: item.workflowItemName || "N/A" },
                    errorMessage: {
                        inputs: {
                            cell: {
                                component: GenericTableFadePreviewComponent,
                                inputs: { text: item.errorMessage || "N/A" },
                            },
                        },
                    },
                    httpResponseCode: { text: item.httpResponseCode?.toString() || "N/A" },
                },
                data: item,
            })),
        };
    };

    async loadHistory() {
        const from = this.fromDate();
        const to = this.toDate();

        if (!from || !to) {
            this.errorMessage.set("Please select a date range");
            return;
        }

        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);

        // Validate that start date is before end date
        if (from > to) {
            this.errorMessage.set("Start date must be before end date");
            return;
        }

        this.errorMessage.set(""); // Clear any previous errors
        try {
            const history = await WorkflowService.getActionHistory(from, to);
            this.history.set(history || []);
        } catch (error) {
            console.error("Failed to load workflow action history:", error);
            this.errorMessage.set("Failed to load workflow action history. Please try again.");
            this.history.set([]);
        }
    }

    refreshTable() {
        if (this.tableComponent) {
            this.tableComponent.refresh();
        }
    }

    private formatDate(dateString?: string) {
        if (!dateString) return "N/A";
        return S25Datefilter.transform(dateString, this.dateFormat) || dateString;
    }
}
